// all-lessons.js - Version: 2.0.0 - Created: 2024-12-08

// داده‌های کامل برای دروس مبتدی
const beginnerLessons = [
  {
    id: 'intro-1',
    title: 'آشنایی با تخته شطرنج',
    description: 'معرفی تخته شطرنج و خانه‌های آن',
    level: 'beginner',
    order: 1,
    duration: '۱۰ دقیقه',
    type: 'interactive',
    thumbnail: '/assets/images/lessons/chess-board-intro.jpg',
    objectives: [
      'شناسایی خانه‌های تخته شطرنج',
      'یادگیری نام‌گذاری خانه‌ها',
      'درک مفهوم ردیف و ستون',
      'شناسایی خانه‌های روشن و تیره'
    ],
    content: {
      video: null,
      text: `
        # آشنایی با تخته شطرنج
        
        ## 🎯 تخته شطرنج چیست؟
        تخته شطرنج یک صفحه مربعی است که از ۶۴ خانه تشکیل شده است. این خانه‌ها به صورت متناوب **روشن** و **تیره** هستند.
        
        ## 📐 ابعاد تخته
        - ۸ ردیف (از پایین به بالا: ۱ تا ۸)
        - ۸ ستون (از چپ به راست: a تا h)
        - در مجموع: ۸ × ۸ = ۶۴ خانه
        
        ## 🎨 رنگ‌بندی خانه‌ها
        - ۳۲ خانه روشن (معمولاً سفید یا بژ)
        - ۳۲ خانه تیره (معمولاً قهوه‌ای یا سبز)
        
        ## 📍 نام‌گذاری خانه‌ها
        هر خانه یک نام منحصر به فرد دارد که از ترکیب **حرف ستون** و **عدد ردیف** ساخته می‌شود.
        
        ### مثال‌ها:
        - خانه a1: گوشه پایین سمت چپ
        - خانه h1: گوشه پایین سمت راست  
        - خانه a8: گوشه بالا سمت چپ
        - خانه h8: گوشه بالا سمت راست
        
        ## 💡 نکته مهم
        همیشه تخته شطرنج را طوری قرار می‌دهیم که **خانه گوشه سمت راست هر بازیکن روشن** باشد.
        
        ## 🎮 تمرین ذهنی
        سعی کن خانه e4 را روی تخته تصور کنی. این خانه در مرکز تخته قرار دارد و از اهمیت زیادی برخوردار است!
      `,
      exercises: [
        {
          id: 'ex-1-1',
          type: 'quiz',
          question: 'تخته شطرنج چند خانه دارد؟',
          options: ['32 خانه', '64 خانه', '16 خانه', '100 خانه'],
          correctAnswer: 1,
          explanation: 'تخته شطرنج ۸ ردیف و ۸ ستون دارد: ۸ × ۸ = ۶۴ خانه'
        },
        {
          id: 'ex-1-2',
          type: 'quiz', 
          question: 'خانه a1 چه رنگی باید باشد؟',
          options: ['همیشه روشن', 'همیشه تیره', 'بستگی به چیدمان دارد', 'سبز'],
          correctAnswer: 0,
          explanation: 'خانه گوشه سمت راست هر بازیکن باید روشن باشد.'
        },
        {
          id: 'ex-1-3',
          type: 'identification',
          title: 'خانه‌ها را شناسایی کن',
          task: 'خانه‌های e4, d5, h1 را روی تخته نشان بده',
          solutions: ['e4', 'd5', 'h1']
        }
      ]
    },
    completed: false,
    locked: false
  },
  {
    id: 'intro-2', 
    title: 'معرفی مهره‌های شطرنج',
    description: 'آشنایی با اسامی، شکل و ارزش مهره‌ها',
    level: 'beginner',
    order: 2,
    duration: '۱۵ دقیقه',
    type: 'interactive',
    thumbnail: '/assets/images/lessons/chess-pieces-intro.jpg',
    objectives: [
      'شناسایی تمام مهره‌های شطرنج',
      'یادگیری شکل و نماد هر مهره',
      'درک ارزش نسبی مهره‌ها',
      'یادگیری جایگاه شروع مهره‌ها'
    ],
    content: {
      video: null,
      text: `
        # معرفی مهره‌های شطرنج
        
        ## ♟️ سرباز (Pawn)
        **نماد:** ♙ (سفید), ♟ (سیاه)
        **تعداد:** ۸ عدد برای هر بازیکن
        **ارزش:** ۱ امتیاز
        
        ### ویژگی‌ها:
        - ضعیف‌ترین مهره اما بسیار مهم
        - در حرکت اول می‌تواند ۲ خانه به جلو برود
        - در حرکت‌های بعدی فقط ۱ خانه به جلو
        - برای گرفتن مهره حریف باید به صورت اریب حرکت کند
        
        ## ♞ اسب (Knight)
        **نماد:** ♘ (سفید), ♞ (سیاه)  
        **تعداد:** ۲ عدد برای هر بازیکن
        **ارزش:** ۳ امتیاز
        
        ### ویژگی‌ها:
        - تنها مهره‌ای که می‌تواند از روی مهره‌ها بپرد
        - به شکل L حرکت می‌کند
        - بسیار قدرتمند در موقعیت‌های بسته
        
        ## ♝ فیل (Bishop)
        **نماد:** ♗ (سفید), ♝ (سیاه)
        **تعداد:** ۲ عدد برای هر بازیکن  
        **ارزش:** ۳ امتیاز
        
        ### ویژگی‌ها:
        - به صورت اریب حرکت می‌کند
        - هر فیل فقط روی خانه‌های هم‌رنگ خودش حرکت می‌کند
        
        ## ♜ رخ (Rook)
        **نماد:** ♖ (سفید), ♜ (سیاه)
        **تعداد:** ۲ عدد برای هر بازیکن
        **ارزش:** ۵ امتیاز
        
        ### ویژگی‌ها:
        - به صورت مستقیم حرکت می‌کند
        - می‌تواند به تعداد نامحدود خانه حرکت کند
        
        ## ♛ وزیر (Queen)
        **نماد:** ♕ (سفید), ♛ (سیاه)
        **تعداد:** ۱ عدد برای هر بازیکن
        **ارزش:** ۹ امتیاز
        
        ### ویژگی‌ها:
        - قدرتمندترین مهره شطرنج
        - ترکیبی از حرکت فیل و رخ
        
        ## ♚ شاه (King)
        **نماد:** ♔ (سفید), ♚ (سیاه)
        **تعداد:** ۱ عدد برای هر بازیکن
        **ارزش:** بی‌نهایت
        
        ### ویژگی‌ها:
        - مهم‌ترین مهره بازی
        - فقط می‌تواند ۱ خانه در هر جهت حرکت کند
      `,
      exercises: [
        {
          id: 'ex-2-1',
          type: 'quiz',
          question: 'کدام مهره می‌تواند از روی مهره‌های دیگر بپرد؟',
          options: ['اسب', 'فیل', 'رخ', 'وزیر'],
          correctAnswer: 0,
          explanation: 'اسب تنها مهره‌ای است که می‌تواند از روی مهره‌های دیگر بپرد.'
        },
        {
          id: 'ex-2-2',
          type: 'quiz',
          question: 'قدرتمندترین مهره شطرنج کدام است؟',
          options: ['شاه', 'وزیر', 'رخ', 'اسب'],
          correctAnswer: 1,
          explanation: 'وزیر می‌تواند مانند فیل و رخ حرکت کند، بنابراین قدرتمندترین مهره است.'
        },
        {
          id: 'ex-2-3',
          type: 'matching',
          title: 'مهره‌ها را به نمادهایشان وصل کن',
          pairs: [
            { piece: 'سرباز', symbol: '♙' },
            { piece: 'اسب', symbol: '♘' },
            { piece: 'فیل', symbol: '♗' },
            { piece: 'رخ', symbol: '♖' },
            { piece: 'وزیر', symbol: '♕' },
            { piece: 'شاه', symbol: '♔' }
          ]
        }
      ]
    },
    completed: false,
    locked: true
  },
  {
    id: 'intro-3',
    title: 'حرکت مهره‌ها - بخش اول',
    description: 'یادگیری حرکت سرباز و رخ',
    level: 'beginner', 
    order: 3,
    duration: '۱۸ دقیقه',
    type: 'interactive',
    thumbnail: '/assets/images/lessons/pawn-rook-moves.jpg',
    objectives: [
      'یادگیری حرکت صحیح سرباز',
      'یادگیری حرکت رخ',
      'تمرین عملی حرکت‌ها',
      'درک محدودیت‌های هر مهره'
    ],
    content: {
      video: null,
      text: `
        # حرکت سرباز و رخ
        
        ## ♙ حرکت سرباز (Pawn)
        
        ### حرکت عادی:
        سرباز فقط می‌تواند **به جلو** حرکت کند!
        
        - در **حرکت اول**: ۱ یا ۲ خانه به جلو
        - در **حرکت‌های بعدی**: فقط ۱ خانه به جلو
        
        ### گرفتن مهره حریف:
        سرباز برای گرفتن مهره حریف باید به صورت **اریب** حرکت کند!
        
        ## ♜ حرکت رخ (Rook)
        
        ### حرکت عادی:
        رخ می‌تواند به صورت **افقی** یا **عمودی** حرکت کند.
        
        - به تعداد **نامحدود** خانه می‌تواند حرکت کند
        - نمی‌تواند از روی مهره‌ها بپرد
        
        ## 💡 نکات طلایی
        
        1. **سربازها** مانند سربازان واقعی هستند - به جلو حرکت می‌کنند!
        2. **رخ‌ها** مانند قلعه‌های متحرک هستند - از دور می‌توانند حریف را تهدید کنند!
        3. همیشه مراقب سربازهای خود باشید - آن‌ها نمی‌توانند به عقب برگردند!
      `,
      exercises: [
        {
          id: 'ex-3-1',
          type: 'quiz',
          question: 'سرباز در حرکت اول چند خانه می‌تواند حرکت کند؟',
          options: ['همیشه ۱ خانه', 'همیشه ۲ خانه', '۱ یا ۲ خانه', '۳ خانه'],
          correctAnswer: 2,
          explanation: 'سرباز در حرکت اول می‌تواند انتخاب کند که ۱ یا ۲ خانه به جلو حرکت کند.'
        },
        {
          id: 'ex-3-2',
          type: 'interactive',
          title: 'سرباز را حرکت بده',
          task: 'سرباز سفید e2 را به خانه e4 حرکت بده',
          solution: 'e2-e4'
        }
      ]
    },
    completed: false,
    locked: true
  }
]

// داده‌های کامل برای دروس متوسط
const intermediateLessons = [
  {
    id: 'inter-1',
    title: 'حرکت شاه و وزیر',
    description: 'یادگیری حرکت دو مهره مهم بازی',
    level: 'intermediate',
    order: 1,
    duration: '۲۰ دقیقه',
    type: 'interactive',
    thumbnail: '/assets/images/lessons/king-queen-moves.jpg',
    objectives: [
      'یادگیری حرکت و محدودیت‌های شاه',
      'تسلط بر حرکت قدرتمند وزیر',
      'درک ارزش و اهمیت شاه',
      'یادگیری تاکتیک‌های مربوط به وزیر'
    ],
    content: {
      video: null,
      text: `
        # حرکت شاه و وزیر
        
        ## ♚ حرکت شاه (King)
        
        ### حرکت اصلی:
        شاه می‌تواند **یک خانه در هر جهت** حرکت کند.
        
        ### محدودیت‌های مهم:
        1. **نمی‌تواند به خانه کیش برود**
        2. **دو شاه نمی‌توانند کنار هم باشند**
        
        ### اهمیت شاه:
        - **مهم‌ترین مهره** بازی
        - در **آغاز بازی** باید در امان باشد
        
        ## ♛ حرکت وزیر (Queen)
        
        ### حرکت ترکیبی:
        وزیر ترکیبی از **رخ و فیل** است.
        
        ### قدرت وزیر:
        - **قدرتمندترین مهره** شطرنج
        - می‌تواند از دور حمله کند
        
        ## 💡 تاکتیک‌های کاربردی
        
        ### با شاه:
        - همیشه شاه را در امان نگه دار
        - در پایان‌بازی، شاه را به مرکز بیاور
        
        ### با وزیر:
        - وزیر را در موقعیت‌های امن قرار بده
        - از وزیر برای ایجاد تهدیدهای چندگانه استفاده کن
      `,
      exercises: [
        {
          id: 'ex-4-1',
          type: 'quiz',
          question: 'شاه در هر نوبت چند خانه می‌تواند حرکت کند؟',
          options: ['۱ خانه', '۲ خانه', '۳ خانه', 'نامحدود'],
          correctAnswer: 0,
          explanation: 'شاه فقط می‌تواند یک خانه در هر جهت حرکت کند.'
        },
        {
          id: 'ex-4-2',
          type: 'quiz',
          question: 'وزیر ترکیبی از کدام دو مهره است؟',
          options: ['اسب و فیل', 'رخ و فیل', 'سرباز و رخ', 'شاه و فیل'],
          correctAnswer: 1,
          explanation: 'وزیر می‌تواند مانند رخ (افقی و عمودی) و مانند فیل (اریب) حرکت کند.'
        }
      ]
    },
    completed: false,
    locked: false
  },
  {
    id: 'inter-2',
    title: 'کیش و مات مقدماتی',
    description: 'یادگیری مفهوم کیش و انواع مات‌های ساده',
    level: 'intermediate', 
    order: 2,
    duration: '۲۲ دقیقه',
    type: 'interactive',
    thumbnail: '/assets/images/lessons/checkmate-basic.jpg',
    objectives: [
      'درک مفهوم کیش و مات',
      'یادگیری مات پشتی',
      'شناسایی مات فولادین',
      'یادگیری دفاع در برابر کیش'
    ],
    content: {
      video: null,
      text: `
        # کیش و مات مقدماتی
        
        ## ⚡ کیش (Check) چیست؟
        
        ### تعریف:
        وقتی مهره‌ای **شاه حریف را تهدید می‌کند**.
        
        ### واکنش به کیش:
        باید یکی از این کارها را انجام دهی:
        1. **شاه را حرکت بده**
        2. **مهره حمله‌کننده را بزن**
        3. **مسیر حمله را سد کن**
        
        ## 💀 مات (Checkmate) چیست؟
        
        ### تعریف:
        وقتی شاه در **کیش** باشد و **هیچ راه فراری** نداشته باشد.
        
        ## 🎯 انواع مات‌های ساده
        
        ### ۱. مات پشتی
        شاه در ردیف اول حبس شده
        
        ### ۲. مات فولادین
        شاه توسط مهره‌های خودش احاطه شده
        
        ## 🛡️ دفاع در برابر کیش
        
        ### روش‌های دفاع:
        1. **همیشه خانه فرار برای شاه بگذار**
        2. **مهره‌های حمله‌کننده را شناسایی کن**
      `,
      exercises: [
        {
          id: 'ex-5-1',
          type: 'quiz',
          question: 'وقتی کیش می‌شوی چه کار باید بکنی؟',
          options: [
            '必須 حرکت قانونی انجام دهی که از کیش خارج شوی',
            'می‌توانی نوبت را رد کنی',
            'فقط می‌توانی شاه را حرکت دهی',
            'هیچ کاری نمی‌توانی بکنی'
          ],
          correctAnswer: 0,
          explanation: 'وقتی کیش می‌شوی باید حتماً حرکت قانونی انجام دهی که شاه از کیش خارج شود.'
        }
      ]
    },
    completed: false,
    locked: true
  }
]

// داده‌های کامل برای دروس پیشرفته
const advancedLessons = [
  {
    id: 'adv-1',
    title: 'گشایش‌های معروف شطرنج',
    description: 'آموزش شروع‌های کلاسیک و مدرن',
    level: 'advanced',
    order: 1,
    duration: '۲۵ دقیقه',
    type: 'interactive',
    thumbnail: '/assets/images/lessons/chess-openings.jpg',
    objectives: [
      'یادگیری گشایش سیسیلی',
      'تسلط بر گشایش روی لوپز',
      'درک اصول گشایش‌ها',
      'یادگیری پاسخ به هر گشایش'
    ],
    content: {
      video: null,
      text: `
        # گشایش‌های معروف شطرنج
        
        ## 🚀 چرا گشایش مهم است؟
        
        ### اهداف گشایش:
        1. **توسعه مهره‌ها**
        2. **کنترل مرکز**
        3. **امنیت شاه**
        4. **ایجاد ساختار پیاده‌ای خوب**
        
        ## 🎯 گشایش سیسیلی (Sicilian Defense)
        
        ### حرکت‌ها:
        1. e4 c5
        
        ### ویژگی‌ها:
        - **محبوب‌ترین گشایش** در سطح حرفه‌ای
        - **شانس برابری** برای سیاه
        
        ## 🎯 گشایش روی لوپز (Ruy Lopez)
        
        ### حرکت‌ها:
        1. e4 e5
        2. Nf3 Nc6
        3. Bb5
        
        ## 💡 اصول طلایی گشایش
        
        ### ۱. توسعه سریع:
        - هر حرکت یک مهره جدید توسعه بده
        
        ### ۲. کنترل مرکز:
        - پیاده‌ها را به مرکز بفرست
        
        ### ۳. قلعه به موقع:
        - معمولاً قبل از حرکت ۱۰ قلعه کن
      `,
      exercises: [
        {
          id: 'ex-6-1',
          type: 'quiz',
          question: 'کدام گشایش با حرکت 1. e4 c5 شروع می‌شود؟',
          options: ['روی لوپز', 'سیسیلی', 'فِرَنچ', 'کارو-کان'],
          correctAnswer: 1,
          explanation: 'گشایش سیسیلی با 1. e4 c5 مشخص می‌شود.'
        }
      ]
    },
    completed: false,
    locked: false
  },
  {
    id: 'adv-2',
    title: 'ترکیب‌های زیبای شطرنج',
    description: 'یادگیری تاکتیک‌های پیچیده و ترکیب‌های دیدنی',
    level: 'advanced',
    order: 2,
    duration: '۲۸ دقیقه',
    type: 'interactive',
    thumbnail: '/assets/images/lessons/chess-combinations.jpg',
    objectives: [
      'یادگیری قربانی کردن مهره',
      'تسلط بر ترکیب‌های کلاسیک',
      'درک موقعیت‌های ترکیبی',
      'یادگیری تشخیص فرصت‌های ترکیبی'
    ],
    content: {
      video: null,
      text: `
        # ترکیب‌های زیبای شطرنج
        
        ## 💎 ترکیب چیست؟
        
        ### تعریف:
        ترکیب یک **سری حرکات اجباری** است که معمولاً با **قربانی کردن مهره** شروع می‌شود.
        
        ### ویژگی‌های ترکیب:
        1. **اجباری**
        2. **قربانی**
        3. **غافلگیرانه**
        4. **زیبا**
        
        ## 🎯 ترکیب‌های معروف
        
        ### ۱. ترکیب آناستازی
        محاصره شاه با اسب و رخ
        
        ### ۲. ترکیب فیلیدور
        مات با رخ و پیاده
        
        ### ۳. ترکیب لگال
        قربانی کردن وزیر برای مات
        
        ## 💡 اصول ترکیب‌سازی
        
        ### ۱. شناسایی weakness:
        - شاه بی‌دفاع
        - مهره‌های توسعه نیافته
      `,
      exercises: [
        {
          id: 'ex-7-1',
          type: 'quiz',
          question: 'ویژگی اصلی یک ترکیب چیست؟',
          options: [
            'حرکات آهسته و position',
            'سری حرکات اجباری با قربانی',
            'توسعه آرام مهره‌ها',
            'مبادله مهره‌های مساوی'
          ],
          correctAnswer: 1,
          explanation: 'ترکیب معمولاً با قربانی شروع می‌شود و شامل حرکات اجباری است.'
        }
      ]
    },
    completed: false,
    locked: true
  }
]

export const allLessons = {
  beginner: beginnerLessons,
  intermediate: intermediateLessons,
  advanced: advancedLessons
}

export const getLessonsByLevel = (level) => {
  return allLessons[level] || []
}

export const getLessonById = (id) => {
  for (const level in allLessons) {
    const lesson = allLessons[level].find(l => l.id === id)
    if (lesson) return lesson
  }
  return null
}